import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useEffect, lazy, Suspense } from "react";

// Lazy-loaded pages for better performance
const LandingPage = lazy(() => import("./pages/LandingPage"));
const SpeakerDashboard = lazy(() => import("./pages/SpeakerDashboard"));
const OrganizerDashboard = lazy(() => import("./pages/OrganizerDashboard"));
const CreateEvent = lazy(() => import("./pages/CreateEvent"));
const SubmitTalk = lazy(() => import("./pages/SubmitTalk"));
const EventReview = lazy(() => import("./pages/EventReview"));
const AgendaBuilder = lazy(() => import("./pages/AgendaBuilder"));
const SpeakerProfile = lazy(() => import("./pages/SpeakerProfile"));
const SpeakerProfileEdit = lazy(() => import("./pages/SpeakerProfileEdit"));
const ReviewerDashboard = lazy(() => import("./pages/ReviewerDashboard"));
const PublicEventPage = lazy(() => import("./pages/PublicEventPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        <span className="text-sm text-gray-400 font-medium">Loading...</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (!loading && !user) {
      sessionStorage.setItem("submitcfp_redirect", location.pathname);
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate, location.pathname]);
  
  if (loading || !user) {
    return <PageLoader />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/speaker" element={<ProtectedRoute><SpeakerDashboard /></ProtectedRoute>} />
        <Route path="/speaker/profile" element={<ProtectedRoute><SpeakerProfileEdit /></ProtectedRoute>} />
        <Route path="/organizer" element={<ProtectedRoute><OrganizerDashboard /></ProtectedRoute>} />
        <Route path="/events/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
        <Route path="/events/:eventId/edit" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
        <Route path="/events/:eventId/submit" element={<ProtectedRoute><SubmitTalk /></ProtectedRoute>} />
        <Route path="/events/:eventId/submissions/:subId/edit" element={<ProtectedRoute><SubmitTalk /></ProtectedRoute>} />
        <Route path="/events/:eventId/review" element={<ProtectedRoute><EventReview /></ProtectedRoute>} />
        <Route path="/events/:eventId/reviewer" element={<ProtectedRoute><ReviewerDashboard /></ProtectedRoute>} />
        <Route path="/events/:eventId/agenda" element={<ProtectedRoute><AgendaBuilder /></ProtectedRoute>} />
        <Route path="/speakers/:speakerId" element={<SpeakerProfile />} />
        <Route path="/cfp/:slug" element={<PublicEventPage />} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ""}>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
